import { FC, useState } from "react"
import { CategoryTag } from "./category-tag"
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui"
import { PlusIcon, AlertTriangle } from "lucide-react"

type Category = {
  id: string
  name: string
}

type CategoryTagListProps = {
  categories: Category[]
  onDeleteCategory: (categoryId: string) => void
  onAddCategory: () => void
  onEditCategory: (categoryId: string) => void
}

export const CategoryTagList: FC<CategoryTagListProps> = ({
  categories,
  onDeleteCategory,
  onAddCategory,
  onEditCategory
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category)
    setShowDeleteDialog(true)
  }

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      onDeleteCategory(categoryToDelete.id)
      setShowDeleteDialog(false)
      setCategoryToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteDialog(false)
    setCategoryToDelete(null)
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <CategoryTag
              key={category.id}
              label={category.name}
              onDelete={() => handleDeleteClick(category)}
              onClick={() => onEditCategory(category.id)}
            />
          ))}

          <Button
            variant="outline"
            size="sm"
            onClick={onAddCategory}
            className="rounded-full border-2 border-sumoPrimary text-sumoPrimary hover:bg-sumoPrimary/10 dark:hover:bg-sumoPrimary/20"
          >
            <PlusIcon width={16} height={16} />
            <span>Add Category</span>
          </Button>
        </div>

        {categories.length === 0 && (
          <p className="text-sm text-neutral500 dark:text-neutral400 italic">
            No categories yet. Click "Add Category" to create your first one.
          </p>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="dark:bg-neutral800 dark:border-neutral700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-error600 dark:text-error400">
              <AlertTriangle width={24} height={24} />
              Delete Category
            </DialogTitle>
            <DialogDescription className="dark:text-neutral400">
              Are you sure you want to delete this category?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-error50 dark:bg-error900/20 border-2 border-error200 dark:border-error800 rounded-lg p-4">
              <p className="text-sm text-error900 dark:text-error200 font-semibold mb-2">
                Category to delete:
              </p>
              <CategoryTag label={categoryToDelete?.name || ""} />
            </div>

            <div className="mt-4 bg-warning50 dark:bg-warning900/20 border border-warning300 dark:border-warning700 rounded-lg p-3">
              <p className="text-sm text-warning900 dark:text-warning200">
                <strong>Warning:</strong> This will clear the category reference from all inventory items using it. Affected items will no longer have a category assigned. This action cannot be undone.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              Delete Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
